/**
 * WebBluetoothTransport — Bitchat-compatible Web Bluetooth client.
 *
 * Web Bluetooth is central-only: it can scan and connect but cannot act as
 * a peripheral. In a mesh context this means the PWA is an OBSERVER/CLIENT
 * of a real Bitchat peer (an iPhone, Android device or ESP32 running the
 * Bitchat firmware). The remote peer acts as our gateway to the rest of
 * the mesh — packets we write flow out through them, and every packet
 * they see is notified to us.
 *
 * Capabilities: canScan=true, canAdvertise=false, canRelay=false.
 *
 * Protocol facts (verified against permissionlesstech/bitchat main):
 *   - Service UUID:        f47b5e2d-4a9e-4c5a-9b3f-8e1d2c3a4b5c (mainnet)
 *   - Characteristic UUID: a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d  (one, bidirectional)
 *   - No pairing / bonding required.
 *   - Central writes packets (prefer writeValueWithoutResponse for throughput).
 *   - Central subscribes to notifications to receive packets.
 *   - iOS pushes its announce ~400 ms after we subscribe.
 *   - BLE notifications do NOT preserve packet boundaries — we feed the raw
 *     byte stream to NotificationStreamAssembler to recover frames.
 *   - Subscribe/unsubscribe in tight loops triggers enumeration-attack
 *     detection and suppresses announces; keep one connection alive.
 *
 * This slice implements the minimum viable flow: scan, connect, subscribe,
 * decode inbound announces + messages, dispatch as peer/packet events.
 * Outbound fragmentation, Ed25519 signing of our announces, PKCS#7 padding
 * and Noise encryption are deferred to later slices.
 */

import { BleTransport } from './BleTransport.js';
import { NotificationStreamAssembler } from '../NotificationStreamAssembler.js';
import { FragmentReassembler } from '../Fragmentation.js';
import { decode as decodePacket, TYPE } from '../MeshPacket.js';
import { decode as decodeAnnounce } from '../AnnouncePacket.js';

const SERVICE_UUID_MAINNET = 'f47b5e2d-4a9e-4c5a-9b3f-8e1d2c3a4b5c';
const SERVICE_UUID_TESTNET = 'f47b5e2d-4a9e-4c5a-9b3f-8e1d2c3a4b5a';
const CHARACTERISTIC_UUID   = 'a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d';

export class WebBluetoothTransport extends BleTransport {
  constructor(opts) {
    super(opts);
    this.device = null;
    this.characteristic = null;
    this.assembler = new NotificationStreamAssembler((bytes) => this._onFrame(bytes));
    this.reassembler = new FragmentReassembler();
    this.gatewayPeerId = null;       // 8-byte ID of the iPhone we're connected to
    this.preferWriteNoResponse = true;
    this._characteristicHandler = null;
    this._gattDisconnectHandler = null;
  }

  get capabilities() {
    return { canScan: true, canAdvertise: false, canRelay: false, name: 'Web Bluetooth (observer)' };
  }

  static isSupported() {
    return typeof navigator !== 'undefined'
        && 'bluetooth' in navigator
        && typeof navigator.bluetooth.requestDevice === 'function';
  }

  async start() {
    if (this.started) return;
    if (!WebBluetoothTransport.isSupported()) {
      throw new Error('Web Bluetooth is not available in this browser');
    }
    // Scanning requires a user gesture — the UI calls requestScan() on
    // click. Nothing to do here except flip the state flag.
    this.started = true;
    console.log('[WebBluetooth] Ready (click "Scan BLE" in the Nearby panel to connect)');
  }

  async stop() {
    if (!this.started) return;
    await this._disconnect();
    this.started = false;
    console.log('[WebBluetooth] Stopped');
  }

  async send(packetBytes, _recipientId) {
    if (!this.characteristic) {
      // Not connected — silently drop. The simulated transport (if present)
      // handles the peer set during development.
      return;
    }
    try {
      if (this.preferWriteNoResponse && this.characteristic.properties.writeWithoutResponse) {
        await this.characteristic.writeValueWithoutResponse(packetBytes);
      } else {
        await this.characteristic.writeValueWithResponse(packetBytes);
      }
    } catch (err) {
      console.warn('[WebBluetooth] Write failed:', err.message);
      // Fall back once to write-with-response and stick with it if that works.
      if (this.preferWriteNoResponse) {
        this.preferWriteNoResponse = false;
        try { await this.characteristic.writeValueWithResponse(packetBytes); }
        catch (err2) { console.warn('[WebBluetooth] Write retry failed:', err2.message); }
      }
    }
  }

  /**
   * User-gesture entry point: opens the browser's Bluetooth chooser, lets
   * the user pick a Bitchat peripheral and connects to it. Resolves once
   * notifications are flowing, rejects on any GATT error.
   */
  async requestScan() {
    if (!this.started) await this.start();
    if (!WebBluetoothTransport.isSupported()) throw new Error('Web Bluetooth unavailable');

    // Tear down any existing connection first. Web Bluetooth only supports
    // one device per transport instance (and iOS rate-limits repeated
    // subscribes — see the BCH-01-004 note).
    await this._disconnect();

    const device = await navigator.bluetooth.requestDevice({
      filters: [
        { services: [SERVICE_UUID_MAINNET] },
        { services: [SERVICE_UUID_TESTNET] },
      ],
    });

    this.device = device;
    this._gattDisconnectHandler = () => this._onGattDisconnect();
    device.addEventListener('gattserverdisconnected', this._gattDisconnectHandler);

    console.log('[WebBluetooth] Connecting to', device.name || device.id);
    const server = await device.gatt.connect();

    let service;
    try { service = await server.getPrimaryService(SERVICE_UUID_MAINNET); }
    catch (_) { service = await server.getPrimaryService(SERVICE_UUID_TESTNET); }

    const characteristic = await service.getCharacteristic(CHARACTERISTIC_UUID);
    this.characteristic = characteristic;
    this.preferWriteNoResponse = !!characteristic.properties.writeWithoutResponse;

    this._characteristicHandler = (ev) => this._onNotification(ev);
    characteristic.addEventListener('characteristicvaluechanged', this._characteristicHandler);
    await characteristic.startNotifications();

    console.log('[WebBluetooth] Subscribed — waiting for announce…');
    return { deviceName: device.name || device.id };
  }

  /** True if currently connected to a Bitchat peripheral. */
  isConnected() {
    return !!(this.device?.gatt?.connected && this.characteristic);
  }

  // ---------- internals ----------

  async _disconnect() {
    if (this.characteristic && this._characteristicHandler) {
      try { await this.characteristic.stopNotifications(); } catch (_) {}
      this.characteristic.removeEventListener('characteristicvaluechanged', this._characteristicHandler);
    }
    if (this.device?.gatt?.connected) {
      try { this.device.gatt.disconnect(); } catch (_) {}
    }
    if (this.device && this._gattDisconnectHandler) {
      this.device.removeEventListener('gattserverdisconnected', this._gattDisconnectHandler);
    }
    this.device = null;
    this.characteristic = null;
    this._characteristicHandler = null;
    this._gattDisconnectHandler = null;
    this.assembler.reset();
    if (this.gatewayPeerId) {
      const lost = this.gatewayPeerId;
      this.gatewayPeerId = null;
      this.handlers.onPeerLost(lost);
    }
  }

  _onGattDisconnect() {
    console.log('[WebBluetooth] GATT disconnected');
    if (this.gatewayPeerId) {
      this.handlers.onPeerLost(this.gatewayPeerId);
      this.gatewayPeerId = null;
    }
    this.characteristic = null;
    this.assembler.reset();
  }

  _onNotification(ev) {
    const value = ev.target.value; // DataView
    if (!value) return;
    const chunk = new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
    this.assembler.feed(chunk);
  }

  /**
   * Called by the stream assembler with each complete packet (raw bytes).
   * Handles reassembly of fragments, then dispatches to MeshClient.
   */
  _onFrame(bytes) {
    let pkt;
    try { pkt = decodePacket(bytes); }
    catch (err) {
      console.warn('[WebBluetooth] Failed to decode packet:', err.message);
      return;
    }

    // Fragments reassemble into an inner packet that is itself a complete
    // BitchatPacket. Recursively decode once we have all pieces.
    if (pkt.type === TYPE.FRAGMENT) {
      const done = this.reassembler.ingest(pkt.senderId, pkt.payload);
      if (!done) return;
      this._onFrame(done.bytes);
      return;
    }

    // Surface the gateway peer (the device we're directly connected to)
    // the first time we see any packet from it.
    if (!this.gatewayPeerId && pkt.type === TYPE.ANNOUNCE) {
      const announce = decodeAnnounce(pkt.payload);
      const nickname = announce.nickname || 'unknown';
      this.gatewayPeerId = pkt.senderId;
      this.handlers.onPeerSeen({
        id: pkt.senderId,
        idHex: _hex(pkt.senderId),
        nickname,
        lastSeen: Date.now(),
        rssi: 0,
        transport: 'web-bluetooth',
      });
    }

    // Also surface peers reached via relays (their announces arrive through
    // the gateway's notifications). These won't have a direct RF link from
    // us but they are addressable through the gateway.
    if (pkt.type === TYPE.ANNOUNCE && _hex(pkt.senderId) !== _hex(this.gatewayPeerId || new Uint8Array(8))) {
      const announce = decodeAnnounce(pkt.payload);
      this.handlers.onPeerSeen({
        id: pkt.senderId,
        idHex: _hex(pkt.senderId),
        nickname: announce.nickname || 'unknown',
        lastSeen: Date.now(),
        rssi: 0,
        transport: 'web-bluetooth (relayed)',
      });
    }

    // Hand every frame up to MeshClient — it does dedup via its own
    // Bloom filter and dispatches by TYPE.
    this.handlers.onPacket(bytes, pkt.senderId);
  }
}

function _hex(u8) {
  return Array.from(u8, b => b.toString(16).padStart(2, '0')).join('');
}

export default WebBluetoothTransport;
